import { DownloadOutlined, FilePdfOutlined } from "@ant-design/icons";
import { Alert, Button, Card, Col, Drawer, Empty, Row, Space, Spin, Statistic, Tag, Typography } from "antd";
import { useMemo } from "react";
import type { ColumnsType } from "antd/es/table";
import type {
  BalanceSheetResult,
  LedgerMonthRow,
  LedgerTransactionRow,
  LedgerVoucherRow,
  ProfitLossResult,
  StatementGroupRow,
  TrialBalanceRow
} from "../../api/reportApi";
import { formatInr } from "../../shared/utils/currency";
import { formatDate } from "../../shared/utils/date";
import { PersistedTable } from "../../shared/components/PersistedTable";

export type PdfViewerState = {
  open: boolean;
  loading: boolean;
  title: string;
  url?: string;
  error?: string;
};

export function MetricCard({ title, value }: { title: string; value: number }) {
  return (
    <Card className="report-metric-card">
      <Statistic title={title} value={value} precision={2} formatter={(val) => formatInr(Number(val ?? 0))} />
    </Card>
  );
}

export function StatementCard({
  title,
  loading,
  leftTitle,
  leftGroups,
  leftTotal,
  rightTitle,
  rightGroups,
  rightTotal,
  footerLabel,
  footerValue
}: {
  title: string;
  loading: boolean;
  leftTitle: string;
  leftGroups: StatementGroupRow[];
  leftTotal: number;
  rightTitle: string;
  rightGroups: StatementGroupRow[];
  rightTotal: number;
  footerLabel: string;
  footerValue: number;
}) {
  return (
    <Card title={title} loading={loading} className="statement-card">
      <Row gutter={[12, 12]}>
        <Col xs={24} md={12}>
          <StatementGroupTable title={leftTitle} total={leftTotal} groups={leftGroups} />
        </Col>
        <Col xs={24} md={12}>
          <StatementGroupTable title={rightTitle} total={rightTotal} groups={rightGroups} />
        </Col>
      </Row>
      <div className="statement-footer">
        <Typography.Text strong>{footerLabel}</Typography.Text>
        <Typography.Text strong>{formatInr(footerValue)}</Typography.Text>
      </div>
    </Card>
  );
}

export function ReportPreviewDrawer({ pdfViewer, onClose }: { pdfViewer: PdfViewerState; onClose: () => void }) {
  return (
    <Drawer
      title={<Space><FilePdfOutlined /> {pdfViewer.title}</Space>}
      open={pdfViewer.open}
      onClose={onClose}
      width="min(1100px, 92vw)"
      extra={pdfViewer.url ? <Button icon={<DownloadOutlined />} onClick={() => triggerUrlDownload(pdfViewer.url!, `${pdfViewer.title}.pdf`)}>Download</Button> : null}
      className="report-preview-drawer"
    >
      {pdfViewer.loading ? (
        <div className="report-preview-state"><Spin size="large" /><Typography.Text type="secondary">Generating report...</Typography.Text></div>
      ) : pdfViewer.error ? (
        <Alert showIcon type="error" message="Report generation failed" description={pdfViewer.error} />
      ) : pdfViewer.url ? (
        <iframe title={pdfViewer.title} src={pdfViewer.url} className="report-preview-frame" />
      ) : null}
    </Drawer>
  );
}

export function LedgerDrillDrawer({
  ledgerDrill,
  monthDrill,
  months,
  transactions,
  setLedgerDrill,
  setMonthDrill
}: {
  ledgerDrill: LedgerVoucherRow | null;
  monthDrill: LedgerMonthRow | null;
  months: { isLoading: boolean; data?: LedgerMonthRow[] };
  transactions: { isLoading: boolean; data?: LedgerTransactionRow[] };
  setLedgerDrill: (row: LedgerVoucherRow | null) => void;
  setMonthDrill: (row: LedgerMonthRow | null) => void;
}) {
  return (
    <Drawer
      title={`Ledger Drilldown - ${ledgerDrill?.name ?? ledgerDrill?.ledgerName ?? ""}`}
      open={!!ledgerDrill}
      onClose={() => setLedgerDrill(null)}
      width="min(1180px, 94vw)"
      className="report-drill-drawer"
    >
      <Row gutter={[12, 12]}>
        <Col xs={24} lg={10}>
          <Card title="Month Summary" extra={<Typography.Text type="secondary">Double-click a month</Typography.Text>}>
            <PersistedTable
              tableId="ledger-drill-months"
              rowKey={(row, index) => `${row.year}-${row.month}-${index}`}
              size="small"
              loading={months.isLoading}
              dataSource={months.data ?? []}
              columns={monthColumns}
              onRow={(row) => ({ onDoubleClick: () => setMonthDrill(row) })}
              rowClassName="report-clickable-row"
              pagination={false}
            />
          </Card>
        </Col>
        <Col xs={24} lg={14}>
          <Card title={monthDrill ? `Transactions - ${monthDrill.monthName ?? `${monthDrill.month}/${monthDrill.year}`}` : "Transactions"}>
            <PersistedTable<LedgerTransactionRow>
              tableId="ledger-drill-transactions"
              rowKey={(row, index) => `${row.entryDate}-${row.voucherRef}-${index}`}
              size="small"
              loading={transactions.isLoading}
              dataSource={monthDrill ? transactions.data ?? [] : []}
              columns={transactionColumns}
              locale={{ emptyText: monthDrill ? <Empty description="No transactions found" /> : <Empty description="Select a month to view transactions" /> }}
              pagination={{ pageSize: 100 }}
            />
          </Card>
        </Col>
      </Row>
    </Drawer>
  );
}

function StatementGroupTable({ title, total, groups }: { title: string; total: number; groups: StatementGroupRow[] }) {
  return (
    <div className="statement-group-table">
      <div className="statement-group-head">
        <Typography.Text strong>{title}</Typography.Text>
        <Tag>{formatInr(total)}</Tag>
      </div>
      <PersistedTable<StatementGroupRow>
        tableId={`statement-group-${title}`}
        rowKey={(row, index) => `${row.groupName}-${index}`}
        size="small"
        dataSource={groups}
        pagination={false}
        columns={[
          { title: "Group", dataIndex: "groupName" },
          { title: "Total", dataIndex: "total", align: "right", width: 130, render: formatInr }
        ]}
        expandable={{
          expandedRowRender: (row) => (
            <PersistedTable
              tableId={`statement-ledgers-${title}-${row.groupName}`}
              rowKey={(line, index) => `${line.ledgerName}-${index}`}
              size="small"
              dataSource={row.ledgers ?? []}
              pagination={false}
              columns={[
                { title: "Ledger", dataIndex: "ledgerName" },
                { title: "Amount", dataIndex: "amount", align: "right", width: 130, render: formatInr }
              ]}
            />
          )
        }}
      />
    </div>
  );
}

export function useStatementTotals(data?: ProfitLossResult | BalanceSheetResult) {
  return useMemo(() => {
    const income = "income" in (data ?? {}) ? sumGroups((data as ProfitLossResult | undefined)?.income) : 0;
    const expense = "expense" in (data ?? {}) ? sumGroups((data as ProfitLossResult | undefined)?.expense) : 0;
    const assets = "assets" in (data ?? {}) ? sumGroups((data as BalanceSheetResult | undefined)?.assets) : 0;
    const liabilities = "liabilities" in (data ?? {}) ? sumGroups((data as BalanceSheetResult | undefined)?.liabilities) : 0;
    return { income, expense, assets, liabilities };
  }, [data]);
}

function sumGroups(groups?: StatementGroupRow[]) {
  return (groups ?? []).reduce((total, group) => total + Number(group.total ?? 0), 0);
}

export function triggerBlobDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  triggerUrlDownload(url, filename);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function triggerUrlDownload(url: string, filename: string) {
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename.replace(/[^\w.-]+/g, "_");
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
}

export const trialColumns: ColumnsType<TrialBalanceRow> = [
  { title: "#", width: 54, render: (_value, _row, index) => index + 1 },
  { title: "Ledger", dataIndex: "ledger" },
  { title: "Debit", dataIndex: "debit", align: "right", width: 150, render: formatInr },
  { title: "Credit", dataIndex: "credit", align: "right", width: 150, render: formatInr },
  { title: "Balance", dataIndex: "balance", align: "right", width: 150, render: (value) => <Typography.Text strong>{formatInr(value)}</Typography.Text> }
];

export const ledgerColumns: ColumnsType<LedgerVoucherRow> = [
  { title: "#", width: 54, render: (_value, _row, index) => index + 1 },
  { title: "Ledger", render: (_, row) => <Space direction="vertical" size={0}><Typography.Text strong>{row.name ?? row.ledgerName ?? `Ledger ${row.id ?? row.ledgerId ?? ""}`}</Typography.Text><Typography.Text type="secondary">{row.accountGroup}</Typography.Text></Space> },
  { title: "Debit", align: "right", width: 150, render: (_, row) => formatInr(row.debitTotal ?? row.debit ?? 0) },
  { title: "Credit", align: "right", width: 150, render: (_, row) => formatInr(row.creditTotal ?? row.credit ?? 0) },
  { title: "Closing", align: "right", width: 160, render: (_, row) => <Typography.Text strong>{formatInr(row.closingBalance ?? row.balance ?? 0)} {row.balanceType}</Typography.Text> }
];

export const monthColumns: ColumnsType<LedgerMonthRow> = [
  { title: "Month", render: (_, row) => row.monthName ?? `${row.month}/${row.year}` },
  { title: "Debit", align: "right", render: (_, row) => formatInr(row.debitTotal ?? row.debit ?? 0) },
  { title: "Credit", align: "right", render: (_, row) => formatInr(row.creditTotal ?? row.credit ?? 0) },
  { title: "Closing", align: "right", render: (_, row) => <Typography.Text strong>{formatInr(row.closingBalance ?? 0)} {row.balanceType}</Typography.Text> }
];

export const transactionColumns: ColumnsType<LedgerTransactionRow> = [
  { title: "Date", width: 110, render: (_, row) => formatDate(row.entryDate ?? row.date) },
  { title: "Voucher", width: 150, render: (_, row) => <Space direction="vertical" size={0}><Typography.Text>{row.voucherType}</Typography.Text><Typography.Text type="secondary">{row.voucherRef}</Typography.Text></Space> },
  { title: "Particulars", render: (_, row) => row.particulars ?? row.narration ?? row.description ?? row.sourceModule },
  { title: "Debit", dataIndex: "debit", align: "right", width: 130, render: formatInr },
  { title: "Credit", dataIndex: "credit", align: "right", width: 130, render: formatInr },
  { title: "Balance", align: "right", width: 150, render: (_, row) => <Typography.Text strong>{formatInr(row.runningBalance ?? row.amount ?? 0)} {row.balanceType}</Typography.Text> }
];
